import { ChangeEvent, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Modal from '@/trio-ui/Modal';
import TransparentButton from '@/trio-ui/TransparentButton';
import * as Styles from './UploadFileRow.styles';

export type UploadFileRowProps = {
  file: File | null;
  inputId?: string;
  uploadedLink?: string;
  acceptedFileTypes?: string;
  onAddFile: (event: ChangeEvent<HTMLInputElement>) => void;
  onDownloadFile?: () => void;
  onRemoveFile: () => void;
};

export default function UploadFileRow({
  file,
  inputId = 'add-file-input',
  acceptedFileTypes,
  onAddFile,
  onDownloadFile,
  onRemoveFile,
}: UploadFileRowProps): JSX.Element {
  const [inputEvent, setInputEvent] = useState<ChangeEvent<HTMLInputElement>>();
  const [showPreview, setShowPreview] = useState<'change' | 'preview'>();
  const [previewUrl, setPreviewUrl] = useState<string>();

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const eventFile = event.target.files?.[0];

    if (eventFile) {
      setInputEvent(event);
      const url = URL.createObjectURL(eventFile);
      setPreviewUrl(url);
      setShowPreview('change');
    }
  };

  const handlePreviewDismiss = (): void => {
    setShowPreview(undefined);
  };

  const handlePreviewClick = (): void => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setShowPreview('preview');
    }
  };

  const InputContent = file ? (
    <Styles.FileUploadedRow>
      <Styles.FileName title={file?.name}>{file?.name}</Styles.FileName>
      <div className="flex spacing-4">
        {onDownloadFile && (
          <TransparentButton onClick={onDownloadFile} icon={DownloadIcon}>
            Download
          </TransparentButton>
        )}
        {file && (
          <TransparentButton onClick={handlePreviewClick} icon={PlayArrowIcon}>
            Watch Preview
          </TransparentButton>
        )}
        <TransparentButton
          onClick={(event) => {
            event.stopPropagation();
            onRemoveFile();
          }}
          isRedButton
          icon={DeleteIcon}
        >
          Delete
        </TransparentButton>
      </div>
    </Styles.FileUploadedRow>
  ) : (
    <Styles.UploadLabel htmlFor={inputId}>
      <input
        id={inputId}
        accept={acceptedFileTypes}
        type="file"
        onChange={handleChange}
        onClick={(e: any) => {
          e.target.value = null;
        }}
      />
      Browse
    </Styles.UploadLabel>
  );

  const handlePreviewConfirm = (): void => {
    if (inputEvent) {
      onAddFile(inputEvent);
    }

    setShowPreview(undefined);
  };

  const ModalBody = <Styles.Video src={previewUrl} controls />;

  return (
    <>
      {InputContent}
      <Modal
        isCentered
        size="4xl"
        bodyRender={ModalBody}
        isOpen={Boolean(showPreview)}
        onClose={handlePreviewDismiss}
        onConfirmLabel="Continue"
        onConfirmClick={showPreview === 'change' ? handlePreviewConfirm : undefined}
        onConfirmButtonVariant="yellow"
        revertButtons
        onCancelLabel={showPreview === 'preview' ? 'Close' : 'Cancel'}
      />
    </>
  );
}
